/**
 * Shelf ("Side shelf", rules v2, Lagos L1661) view helpers, free of Phaser so they are unit-tested
 * in node: which pieces are a shelf's, what its row shows, its `shelved` events, and where the row
 * sits in the strip beside the board. Core decides every rule; these only read its compiled level,
 * state and events.
 *
 * Piece order (core CompiledLevel): board tiles, crates, conveyor entries, dispenser items, tray
 * pieces, then every shelf's items left to right (shelfStart[s] .. shelfStart[s + 1] - 1). A shelf
 * is off the board grid like the conveyor (no cell, no cover), so it takes the strip the conveyor
 * uses (a level never has both). Every item keeps its own place on the row for the whole round: a
 * taken item leaves a gap there (undo returns it to the same place), so the row never slides. Only
 * its two ends, the first and the last item still on the row, are tap targets; core reports the
 * new ends after every take (event `shelved`).
 */
import { PIECE_BOARD, shelfEnds, shelfLeft, type CompiledLevel, type Round, type RoundEvent } from '@mp/core';
import type { Rect } from '../game/layout.js';

/** Shelf index of piece i, or -1. */
export function shelfOf(L: CompiledLevel, i: number): number {
  return L.nShelves ? (L.shelfOf[i] ?? -1) : -1;
}

/** Is piece i an item on a shelf's row (not a board cell, not the conveyor)? */
export const isShelfPiece = (L: CompiledLevel, i: number): boolean => shelfOf(L, i) >= 0;

/** Piece i's place on its shelf's row, 0 at the left end. */
export const shelfSlot = (L: CompiledLevel, i: number): number => i - L.shelfStart[L.shelfOf[i]!]!;

/** Shelf s's items, left to right (every one, on the row or not). */
export function shelfPieces(L: CompiledLevel, s: number): number[] {
  const out: number[] = [];
  for (let i = L.shelfStart[s]!; i < L.shelfStart[s + 1]!; i++) out.push(i);
  return out;
}

export interface ShelfState {
  /** items still on the row, left to right */
  shown: number[];
  /** core's ends: the first and the last item still on the row ([-1, -1] once it is empty) */
  ends: [number, number];
  /** items still on the row */
  left: number;
}

export function shelfState(r: Round, s: number): ShelfState {
  return { shown: shelfPieces(r.L, s).filter((i) => r.piece[i] === PIECE_BOARD), ends: shelfEnds(r, s), left: shelfLeft(r, s) };
}

/** Is piece i one of its shelf's ends now (a tap target; the items between them are not)? */
export function isShelfEnd(r: Round, i: number): boolean {
  const s = shelfOf(r.L, i);
  if (s < 0 || r.piece[i] !== PIECE_BOARD) return false;
  const [a, b] = shelfEnds(r, s);
  return i === a || i === b;
}

/** The `shelved` events of a batch (one per item a tap or a magnet took off a shelf). */
export function shelvedEvents(events: readonly RoundEvent[]): Extract<RoundEvent, { type: 'shelved' }>[] {
  return events.filter((e): e is Extract<RoundEvent, { type: 'shelved' }> => e.type === 'shelved');
}

/** Where a shelf's row sits in its strip (device px). */
export interface ShelfGeometry {
  /** item tile edge */
  size: number;
  /** centre-to-centre distance between two places on the row */
  step: number;
  /** centre x of the row's first place */
  x0: number;
  /** centre y of an item resting on the plank (an end rises `lift` above it) */
  y: number;
  /** how far an end item is lifted off the plank */
  lift: number;
  /** the plank's top face: the row stands on it */
  plank: Rect;
}

/**
 * The row of `n` places (MIN..MAX_SHELF_ITEMS) centred in the strip `area`, every place inside
 * it. Items are as large as the strip's height allows, never wider than their place, so eight
 * items still fit a 360 px phone (each about 38 px, a little gap between them); `u` is the
 * CSS-to-device scale.
 */
export function shelfGeometry(area: Rect, n: number, u: number): ShelfGeometry {
  const places = Math.max(1, n);
  const maxStep = (area.w - 12 * u) / places;
  const size = Math.min(area.h * 0.7, maxStep * 0.92, 72 * u);
  const step = Math.min(maxStep, size * 1.14);
  const x0 = area.x + area.w / 2 - (step * (places - 1)) / 2;
  const y = area.y + area.h * 0.5;
  const lift = size * 0.14;
  const pad = Math.max(6 * u, step * 0.18);
  const left = x0 - step / 2 - pad;
  const right = x0 + step * (places - 1) + step / 2 + pad;
  const top = y + size * 0.42;
  return { size, step, x0, y, lift, plank: { x: Math.max(area.x, left), y: top, w: Math.min(area.x + area.w, right) - Math.max(area.x, left), h: Math.max(6 * u, area.h * 0.12) } };
}

/** Centre of row place k (an end: lifted). */
export function placeCentre(g: ShelfGeometry, k: number, end: boolean): { x: number; y: number } {
  return { x: g.x0 + k * g.step, y: g.y - (end ? g.lift : 0) };
}

/** A box edge belongs to the box: float rounding must not drop the point that lies exactly on it (device px). */
const EDGE = 1e-6;

/**
 * The row place a point means, or -1. An end (`ends`: the slots of the first and the last item
 * still on the row) answers to the box round its lifted item that is the wider of its place and
 * `minTarget` (device px: 44 CSS px, PRD §6) across and the taller of its item and `minTarget`
 * high, and a point inside it is that end's whatever else lies there: the items between the ends
 * are never tappable, so an end may take its 44 px over its inner neighbour or a gap beside it
 * (eight items on a 360 px phone are 41 px apart). Ends whose boxes overlap go to the nearer
 * centre. Only a point outside every end's box falls to the nearest other place within half a
 * step and the item's height, which the caller refuses with a wobble (a gap answers -1 there).
 */
export function placeAt(g: ShelfGeometry, n: number, x: number, y: number, ends: readonly number[], minTarget: number): number {
  let best = -1;
  let bestD = Infinity;
  for (const k of ends) {
    const c = placeCentre(g, k, true);
    if (Math.abs(x - c.x) > Math.max(g.step, minTarget) / 2 + EDGE || Math.abs(y - c.y) > Math.max(g.size, minTarget) / 2 + EDGE) continue;
    const d = Math.abs(x - c.x);
    if (d < bestD) {
      bestD = d;
      best = k;
    }
  }
  if (best >= 0) return best;
  for (let k = 0; k < n; k++) {
    if (ends.includes(k)) continue;
    const c = placeCentre(g, k, false);
    if (Math.abs(x - c.x) > g.step / 2 + EDGE || Math.abs(y - c.y) > g.size / 2 + EDGE) continue;
    const d = Math.abs(x - c.x);
    if (d < bestD) {
      bestD = d;
      best = k;
    }
  }
  return best;
}
